import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { filter, first, take, timeout } from 'rxjs';
import { AuthenticationService } from 'src/app/_core/_services/authentication.service';
import { UserService } from 'src/app/_share/_services/user.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {
  loading = false;
  submitted = false;
  error = '';
  loginForm!: FormGroup;
  showPassword = false;
  rememberMe = false;

  constructor(
    private authenticationService: AuthenticationService,
    private userService: UserService,
    private router: Router,
    private formBuilder: FormBuilder,
    private route: ActivatedRoute
  ) {
    // redirect to home if already logged in
    if (this.authenticationService.userValue) {
      this.router.navigate(['/']);
    }
  }

  ngOnInit(): void {
    // create the form
    this.loginForm = this.formBuilder.group({
      username: ['', [Validators.required, Validators.minLength(3)]],
      password: ['', [Validators.required, Validators.minLength(6)]]
    });

    this.loginForm.valueChanges.subscribe(() => {
      this.error = '';
    });
  }

  // convenience getter for easy access to form fields
  get f() {
    return this.loginForm.controls;
  }

  // Check if a field is invalid and should show error
  isFieldInvalid(fieldName: string): boolean {
    const field = this.loginForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched || this.submitted));
  }

  // Get error message for a field
  getFieldError(fieldName: string): string {
    const field = this.loginForm.get(fieldName);
    if (!field || !field.errors) return '';

    if (field.errors['required']) {
      return `${this.getFieldLabel(fieldName)} is required`;
    }
    if (field.errors['minlength']) {
      const requiredLength = field.errors['minlength'].requiredLength;
      return `${this.getFieldLabel(fieldName)} must be at least ${requiredLength} characters`;
    }
    if (field.errors['email']) {
      return 'Please enter a valid email address';
    }

    return 'Invalid input';
  }

  // Get human-readable field label
  private getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      username: 'Username',
      password: 'Password',
      email: 'Email'
    };
    return labels[fieldName] || fieldName;
  }

  // Toggle password visibility
  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  // Every role lands on /home after login - it shows only the areas/stats
  // relevant to the user's actual role. Kept as a named method (matching
  // AuthGuard.redirectBasedOnRole) in case a role ever needs a different
  // landing page again.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private navigateByRole(role: string): void {
    this.router.navigate(['/home']);
  }

  onSignupClick(event: Event): void {
    event.preventDefault();
    // You can implement signup logic here or show a message
    console.log('Signup functionality coming soon');
  }

  onSubmit() {
    this.submitted = true;

    // stop here if form is invalid
    if (this.loginForm.invalid) {
      // mark all fields as touched
      Object.keys(this.loginForm.controls).forEach((key) => {
        this.loginForm.controls[key].markAsTouched();
        this.loginForm.controls[key].markAsDirty();
      });
      // Focus on username field using native element
      const usernameElement = document.getElementById('username') as HTMLInputElement;
      if (usernameElement) {
        usernameElement.focus();
      }
      return;
    }

    this.loading = true;
    this.authenticationService
      .login(this.f['username'].value, this.f['password'].value)
      .pipe(first())
      .subscribe({
        next: () => {
          const returnUrl = this.route.snapshot.queryParams['returnUrl'];
          if (returnUrl) {
            this.router.navigateByUrl(returnUrl);
          } else {
            this.userService.getCurrentLoggedInUser();
            this.userService.currentUser$
              .pipe(
                // roles[0] is the current field; role is deprecated but
                // kept as a fallback for compat.
                filter((user: any) => !!(user?.roles?.[0]?.name || user?.role?.name)),
                take(1),
                // Backstop: if currentUser$ never emits a value with a role
                // (a failed /users/me call, a user with no roles assigned,
                // any future variant of this failure) the spinner must not
                // hang forever with no explanation.
                timeout(10000)
              )
              .subscribe({
                next: (user: any) => {
                  this.navigateByRole(user?.roles?.[0]?.name || user?.role?.name);
                },
                error: () => {
                  this.loading = false;
                  this.error =
                    'Signed in, but could not load your account details. Please try again.';
                }
              });
          }
        },
        error: (response) => {
          // console.log(response);
          const error = response.error.detail;
          this.error = error;
          this.loading = false;
        }
      });
  }
}
